import { useEffect, useState } from "react";
import { X, GlobeHemisphereEast, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "traina.tz_dismissed_until";

/**
 * Travel-mode banner: see comments below.
 */
export default function TimezoneBanner() {
  const { user } = useAuth();
  const [stored, setStored] = useState(null);
  const [detected] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch (_e) { return "UTC"; }
  });
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || ""; }
    catch (_e) { return ""; }
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id || user.role !== "trainer") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/profile/timezone");
        if (cancelled) return;
        const curr = data?.timezone || "UTC";
        if (curr === "UTC" && detected && detected !== "UTC") {
          try {
            await api.put("/profile/timezone", { timezone: detected });
            if (!cancelled) setStored(detected);
            return;
          } catch (_e) { /* fall through */ }
        }
        if (!cancelled) setStored(curr);
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.role, detected]);

  const switchNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile/timezone", { timezone: detected });
      setStored(data.timezone);
      toast.success(`Timezone updated to ${data.timezone}`);
    } catch (_e) {
      toast.error("Couldn't update timezone");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, detected); } catch (_e) { /* ignore */ }
    setDismissed(detected);
  };

  if (!user || user.role !== "trainer") return null;
  if (!stored) return null;
  if (!detected || detected === stored) return null;
  if (dismissed === detected) return null;

  return (
    <div
      className="border-b border-brand/40 bg-brand/10"
      data-testid="travel-mode-banner"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <GlobeHemisphereEast size={18} weight="duotone" className="text-brand-light shrink-0" />
          <p className="text-xs sm:text-sm text-zinc-200 min-w-0">
            <span className="tracker text-brand-light mr-2">Travel mode</span>
            Looks like you&apos;re in <span className="font-mono text-white">{detected}</span> now —
            saved zone is <span className="font-mono text-white">{stored}</span>.
            <span className="hidden md:inline text-zinc-500"> Sessions on the calendar will be shown in your saved zone until you switch.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={switchNow}
            disabled={busy}
            className="btn-primary !py-1.5 !px-3 !text-[10px]"
            data-testid="travel-mode-switch"
          >
            {busy ? "Switching…" : <>Switch to {detected} <ArrowRight size={12} weight="bold" /></>}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="btn-ghost !py-1.5 !px-2"
            aria-label="Dismiss"
            title="Dismiss for this session"
            data-testid="travel-mode-dismiss"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
